/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

function count<T extends string | number>(
	items: readonly T[],
): Record<string, number> {
	const counts: Record<string, number> = {};
	items.forEach((item) => {
		const itemKey = item.toString();
		if (!(itemKey in counts)) {
			counts[itemKey] = 0;
		}
		counts[itemKey] += 1;
	});
	return counts;
}

function findOnlyOrThrow<T>(
	items: readonly T[],
	predicate: (item: T) => boolean,
): T {
	const matches = items.filter(predicate);
	if (matches.length === 0) {
		throw new Error("No result");
	}
	if (matches.length > 1) {
		throw new Error("Multiple results");
	}
	return matches[0];
}

function firstOrThrow<T>(items: readonly T[], message: string): T {
	if (items.length === 0) {
		throw new Error(message);
	}
	return items[0];
}

function initial<T>(items: readonly T[]): readonly T[] {
	return items.slice(0, items.length - 1);
}

function last<T>(items: readonly T[]): T | undefined {
	return items[items.length - 1];
}

function createMapper<T>(source: ((item: T) => unknown) | string) {
	if (typeof source === "function") {
		return source;
	}

	return (item: T) => (item as any)[source];
}

type DirectionMapper<T> = {
	readonly mapper: (item: T) => number | string;
	readonly direction: "asc" | "desc";
};

function createDirectionMapper<T>(
	source: ((item: T) => unknown) | string,
): DirectionMapper<T> {
	if (typeof source === "function") {
		return {
			direction: "asc",
			mapper: source as any,
		};
	}

	if (source.charAt(0) === "-") {
		return {
			direction: "desc",
			mapper: (item: T) => (item as any)[source.substring(1)],
		};
	}

	return {
		direction: "asc",
		mapper: (item: T) => (item as any)[source],
	};
}

// TODO: Should include 0n - but causes ts errors that didn't have time to track down
// eslint-disable-next-line no-null/no-null
const falseyValues = [null, undefined, false, "", 0] as const;

type Falsey = (typeof falseyValues)[number];

function compact<T>(items: (T | Falsey)[] | readonly (T | Falsey)[]): T[] {
	return items.filter(
		(i) => !(falseyValues as readonly unknown[]).includes(i),
	) as T[];
}

function max(items: readonly number[]): number | undefined {
	let value: number | undefined;
	for (const v of items) {
		if (value === undefined || v > value) {
			value = v;
		}
	}
	return value;
}

function maxBy<T>(
	items: readonly T[],
	mapper: (item: T) => number,
): T | undefined {
	let current: { max: number; value: T } | undefined;
	for (const i of items) {
		const iMax = mapper(i);
		if (current === undefined || iMax > current.max) {
			current = { max: iMax, value: i };
		}
	}
	return current?.value;
}

function range(
	startOrEnd: number,
	possibleEnd?: number,
	step: number = 1,
): readonly number[] {
	const start = possibleEnd === undefined ? 0 : startOrEnd;
	const end = possibleEnd === undefined ? startOrEnd : possibleEnd;
	return Array.from(
		{ length: Math.ceil((end - start) / step) },
		(_, i) => start + i * step,
	);
}

function chunk<T>(items: readonly T[], amount: number): readonly T[][] {
	const numChunks = amount === 0 ? 0 : Math.ceil(items.length / amount);
	return range(0, numChunks).map((i) =>
		items.slice(i * amount, (i + 1) * amount),
	);
}

type BaseMapper<T> = ((item: T) => number | string) | string;

// Couldn't make work
// keyof T | `-${keyof T}`
function sortBy<T>(
	items: readonly T[],
	mapper: BaseMapper<T> | readonly BaseMapper<T>[],
): readonly T[] {
	const mappers = Array.isArray(mapper) ? mapper : [mapper];
	// TODO: as any cast only needed until all workspaces updated to ts 6
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
	const useMappers = mappers.map((m) => createDirectionMapper<T>(m as any));

	function compare(mappersRest: readonly DirectionMapper<T>[], a: T, b: T) {
		if (mappersRest.length === 0) {
			return 0;
		}

		const [mapper, ...nextMappersRest] = mappersRest;
		const mA = mapper.mapper(a);
		const mB = mapper.mapper(b);
		const directionMultiplier = mapper.direction === "asc" ? 1 : -1;
		if (mA < mB) {
			return -1 * directionMultiplier;
		}
		if (mA > mB) {
			return 1 * directionMultiplier;
		}
		return compare(nextMappersRest, a, b);
	}

	return items.slice().sort((a, b) => compare(useMappers, a, b));
}

function unzip<T>(items: readonly T[][]): readonly T[][] {
	if (items.length === 0) {
		return [];
	}

	const maxLength = Math.max(...items.map((i) => i.length));
	const itemIndices = range(0, items.length);
	return range(0, maxLength).map((i) => itemIndices.map((j) => items[j][i]));
}

function uniq<T>(items: readonly T[]): T[] {
	if (items.length === 0) {
		return [];
	}
	return Array.from(new Set(items));
}

function uniqBy<T, TR>(
	items: readonly T[],
	mapper: ((item: T) => TR) | string,
): T[] {
	if (items.length === 0) {
		return [];
	}

	const newItems: T[] = [];
	const seen = new Set<TR>();
	const useMapper = createMapper(mapper);
	for (const item of items) {
		const by = useMapper(item);
		if (!seen.has(by)) {
			seen.add(by);
			newItems.push(item);
		}
	}
	return newItems;
}

function intersection<T>(a1: readonly T[], a2: readonly T[]): readonly T[] {
	return a1.filter((i1) => a2.includes(i1));
}

function difference<T>(a1: readonly T[], a2: readonly T[]): readonly T[] {
	return a1.filter((i1) => !a2.includes(i1));
}

function groupBy<T, TR extends string | number | undefined>(
	items: readonly T[],
	mapper: ((item: T) => TR) | string,
): { [index: string]: readonly T[] } {
	const useMapper = createMapper(mapper);
	const result = {} as { [key in Exclude<TR, undefined>]: T[] };
	for (const i of items) {
		const value = useMapper(i);
		if (!(value in result)) {
			(result as any)[value] = [];
		}
		(result as any)[value].push(i);
	}
	return result;
}

function partition<T>(
	collection: readonly T[],
	callback: (item: T) => boolean,
): Readonly<[readonly T[], readonly T[]]> {
	const matches = [] as T[];
	const nonMatches = [] as T[];
	for (const item of collection) {
		if (callback(item)) {
			matches.push(item);
		} else {
			nonMatches.push(item);
		}
	}
	return [matches, nonMatches];
}

export {
	findOnlyOrThrow,
	partition,
	groupBy,
	uniqBy,
	uniq,
	unzip,
	maxBy,
	sortBy,
	chunk,
	initial,
	max,
	last,
	range,
	compact,
	intersection,
	difference,
	count,
	firstOrThrow,
};
